import React, { useEffect, useMemo, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { Download, Layers, List as ListIcon, Trash2 } from "lucide-react";

export default function AdminSubmissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grouped, setGrouped] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/submissions"); setItems(data.submissions || []); }
      finally { setLoading(false); }
    })();
  }, []);

  const deleteSubmission = async (s) => {
    if (!window.confirm(
      `Delete this "${s.form_title}" submission from ${s.user_name || "this seller"}?\n\n` +
      "Their draft stays filled in, and the form on their side will go back to 'Not yet' so they can finish and resubmit. " +
      "This can't be undone — the PDF copy will be removed too."
    )) return;
    setBusyId(s.submission_id); setErr("");
    try {
      await api.delete(`/admin/submissions/${s.submission_id}`);
      setItems((xs) => xs.filter((x) => x.submission_id !== s.submission_id));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId("");
    }
  };

  const groups = useMemo(() => {
    const by = new Map();
    for (const s of items) {
      const key = s.household_id || s.user_id;
      if (!by.has(key)) by.set(key, []);
      by.get(key).push(s);
    }
    // newest household first based on most recent submission
    return Array.from(by.entries())
      .map(([hid, subs]) => {
        subs.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        const first = subs[0];
        const names = Array.from(new Set(subs.map((s) => s.user_name).filter(Boolean))).join(" & ");
        return {
          hid,
          subs,
          headerName: names || first?.user_name || "—",
          property: first?.property_address || "",
          latest: first?.created_at || "",
        };
      })
      .sort((a, b) => (b.latest || "").localeCompare(a.latest || ""));
  }, [items]);

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="overline">Admin · All submissions</span>
          <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
            All submissions
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button data-testid="group-toggle-on" onClick={() => setGrouped(true)} className={grouped ? "btn-secondary" : "btn-ghost"} style={{ padding: "8px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Layers size={14} /> Group by client
          </button>
          <button data-testid="group-toggle-off" onClick={() => setGrouped(false)} className={!grouped ? "btn-secondary" : "btn-ghost"} style={{ padding: "8px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ListIcon size={14} /> All in one list
          </button>
        </div>
      </div>

      {err && <div data-testid="admin-subs-err" style={{ marginTop: 16, padding: "10px 14px", borderRadius: 10, background: "#F7E2E2", color: "var(--error)", fontSize: 13 }}>{err}</div>}

      {loading ? (
        <div className="card" style={{ padding: 24, color: "var(--text-muted)", marginTop: 24 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="card" style={{ padding: 24, color: "var(--text-muted)", marginTop: 24 }}>No submissions yet.</div>
      ) : grouped ? (
        <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 18 }} data-testid="grouped-submissions">
          {groups.map((g) => (
            <div key={g.hid} className="card" style={{ overflow: "hidden" }} data-testid={`sub-group-${g.hid}`}>
              <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>{g.headerName}</div>
                    {g.property && <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{g.property}</div>}
                  </div>
                  <span className="badge">{g.subs.length} submission{g.subs.length === 1 ? "" : "s"}</span>
                </div>
              </div>
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "var(--bg)" }}>
                      {["Form", "Submitted by", "When", "Email", ""].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "10px 20px", fontWeight: 600, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {g.subs.map((s) => (
                      <tr key={s.submission_id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`sub-row-${s.submission_id}`}>
                        <td style={{ padding: "12px 20px", fontWeight: 500 }}>{s.form_title}</td>
                        <td style={{ padding: "12px 20px", color: "var(--text-muted)" }}>{s.user_name}</td>
                        <td style={{ padding: "12px 20px", color: "var(--text-muted)" }}>{new Date(s.created_at).toLocaleString()}</td>
                        <td style={{ padding: "12px 20px" }}>
                          {s.email_status?.sent ? <span className="badge badge-success">Sent</span> : <span className="badge badge-warning">Pending</span>}
                        </td>
                        <td style={{ padding: "12px 20px" }}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <a data-testid={`pdf-dl-${s.submission_id}`} className="btn-ghost" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--primary)" }} href={`${API}/forms/submission/${s.submission_id}/pdf`} target="_blank" rel="noreferrer">
                              <Download size={14} /> PDF
                            </a>
                            <button
                              data-testid={`sub-delete-${s.submission_id}`}
                              type="button"
                              className="btn-ghost"
                              onClick={() => deleteSubmission(s)}
                              disabled={busyId === s.submission_id}
                              title="Delete this submission — the seller's form goes back to 'Not yet' so they can finish and resubmit."
                              style={{ color: "var(--error)", padding: "6px 8px" }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, marginTop: 24, overflow: "hidden" }} data-testid="all-submissions-table">
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
              <thead>
                <tr style={{ background: "var(--surface-alt)" }}>
                  {["Form", "Seller", "Property", "Submitted", "Email", ""].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "12px 20px", fontWeight: 600, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.submission_id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`sub-row-${s.submission_id}`}>
                    <td style={{ padding: "14px 20px", fontWeight: 500 }}>{s.form_title}</td>
                    <td style={{ padding: "14px 20px", color: "var(--text-muted)" }}>{s.user_name}<div style={{ fontSize: 12 }}>{s.user_email}</div></td>
                    <td style={{ padding: "14px 20px", color: "var(--text-muted)" }}>{s.property_address || "—"}</td>
                    <td style={{ padding: "14px 20px", color: "var(--text-muted)" }}>{new Date(s.created_at).toLocaleString()}</td>
                    <td style={{ padding: "14px 20px" }}>
                      {s.email_status?.sent ? <span className="badge badge-success">Sent</span> : <span className="badge badge-warning">Pending</span>}
                    </td>
                    <td style={{ padding: "14px 20px" }}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <a data-testid={`pdf-dl-${s.submission_id}`} className="btn-ghost" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--primary)" }} href={`${API}/forms/submission/${s.submission_id}/pdf`} target="_blank" rel="noreferrer">
                          <Download size={14} /> PDF
                        </a>
                        <button
                          data-testid={`sub-delete-${s.submission_id}`}
                          type="button"
                          className="btn-ghost"
                          onClick={() => deleteSubmission(s)}
                          disabled={busyId === s.submission_id}
                          title="Delete this submission — the seller's form goes back to 'Not yet' so they can finish and resubmit."
                          style={{ color: "var(--error)", padding: "6px 8px" }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
